import axios from 'axios';
import { ApiSalesDashboardService } from 'services';
import { IAgingData, IAgingInvoicesData, IAgingOrdersData, Segmento, TipoOrden } from './types';

interface KpiEnvelope<T> {
	success: boolean;
	data: T;
	metadata?: { cachedAt?: string; [k: string]: unknown };
}

type Params = Record<string, string | number | boolean | undefined>;

async function kpiGet<T>(path: string, params?: Params): Promise<{ data: T; cachedAt: string | null }> {
	const clean = params
		? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== ''))
		: undefined;
	const res = await ApiSalesDashboardService.get<KpiEnvelope<T>>(path, { params: clean });
	if (!res.data.success) throw new Error(`API error: success=false on ${path}`);
	return { data: res.data.data, cachedAt: res.data.metadata?.cachedAt ?? null };
}

export function extractReceivablesError(err: unknown): string {
	if (axios.isAxiosError(err)) {
		const status = err.response?.status;
		const data = err.response?.data as { message?: string } | undefined;
		if (status && [400, 403, 404, 422].includes(status)) return data?.message ?? `Error ${status}`;
		if (status === 401) return 'Tu sesión expiró — volvé a iniciar sesión';
		if (status && status >= 500) return `Error del servidor (${status}) — intentá de nuevo`;
		if (err.request) return 'No se pudo conectar con el servidor — verificá tu conexión';
	}
	if (err instanceof Error) return err.message;
	return 'Error inesperado';
}

const BASE = 'data/sales/receivables';

/** El árbol completo: agencias → clientes, con buckets y órdenes sin definir. */
export const getAging = (options?: {
	segments?: Segmento[];
	agencyId?: number;
	asOf?: string;
	refresh?: boolean;
}): Promise<{ data: IAgingData; cachedAt: string | null }> =>
	kpiGet<IAgingData>(`${BASE}/aging`, {
		segments: options?.segments?.length ? options.segments.join(',') : undefined,
		agencyId: options?.agencyId,
		asOf: options?.asOf,
		refresh: options?.refresh,
	});

/** Las facturas detrás de una celda de facturación. */
export const getAgingInvoices = async (options: {
	bucket: string;
	passengerId?: string;
	agencyId?: number;
	segments?: Segmento[];
	asOf?: string;
}): Promise<IAgingInvoicesData> => {
	const { data } = await kpiGet<IAgingInvoicesData>(`${BASE}/aging/invoices`, {
		...options,
		segments: options.segments?.length ? options.segments.join(',') : undefined,
	});
	return data;
};

/** Las órdenes detrás de cobranza / saldo a favor / pendiente de facturar. */
export const getAgingOrders = async (options: {
	tipo: TipoOrden;
	passengerId?: string;
	agencyId?: number;
	segments?: Segmento[];
}): Promise<IAgingOrdersData> => {
	const { data } = await kpiGet<IAgingOrdersData>(`${BASE}/aging/orders`, {
		...options,
		segments: options.segments?.length ? options.segments.join(',') : undefined,
	});
	return data;
};
